import { DateTime } from "luxon";
import axios, { AxiosResponse, CancelTokenSource, InternalAxiosRequestConfig } from "axios";

import { ActionResponse, ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { Cancelled } from "../../types/ErrorTypes";
import { AuthState } from "../../state/reducers/authReducer";
import { API_BASE_URL } from "../../config";
import { parseDateTimes } from "../../utilities/helpers/parseDateTimes";
import { classify401 } from "./authRetry";
import { markServerReachable, markServerUnreachable } from "../../offline/connectivity";
import i18n, { currentLanguage } from "../../i18n";
import { translateServerErrors } from "../../i18n/serverMessages";

type TimedRequestConfig = InternalAxiosRequestConfig & { startedAt?: number };

export type RequestOptions = { timeoutMs?: number; headers?: Record<string, string> };
export type WriteOptions<T> = RequestOptions & { transformRequest?: (data: T) => T };
export type PostOptions = RequestOptions & { skipAuth?: boolean; onHeaders?: (headers: AxiosResponse["headers"]) => void };

export class AjaxController {
  private baseUrl = API_BASE_URL;
  private cancelTokenSources: { [key: string]: CancelTokenSource } = {};
  private getAuthState?: () => AuthState | null;
  private refreshTokenCallback?: () => Promise<{ accessToken?: string } | null | undefined>;
  private refreshPromise: Promise<{ accessToken: string } | null> | null = null;
  private authFailureCallback?: () => void;
  private authFailureNotified = false;
  private client = axios.create({ baseURL: this.baseUrl });

  constructor() {
    this.client.interceptors.request.use((config) => {
      if (config.data && !(config.data instanceof FormData)) {
        config.data = convertDatesToUTC(config.data);
      }
      (config as TimedRequestConfig).startedAt = performance.now();
      config.headers.set("Accept-Language", currentLanguage());
      return config;
    });

    this.client.interceptors.response.use(
      originalResponse => {
        this.authFailureNotified = false;
        markServerReachable();
        parseDateTimes(originalResponse.data);
        translateServerErrors(originalResponse.data);
        return originalResponse;
      },
      async (error) => {
        if (axios.isAxiosError(error) && error.response) {
          markServerReachable();
        } else if (axios.isAxiosError(error) && error.code !== "ERR_CANCELED") {
          markServerUnreachable((error.config as TimedRequestConfig | undefined)?.startedAt);
        }

        const original = error.config as (InternalAxiosRequestConfig & { _authRetried?: boolean }) | undefined;
        const strategy = classify401({
          isUnauthorized: axios.isAxiosError(error) && error.response?.status === 401,
          hasConfig: !!original,
          url: original?.url,
          alreadyRetried: !!original?._authRetried,
        });

        if (strategy === "pass-through") return Promise.reject(error);
        if (strategy === "give-up") {
          this.notifyAuthFailure();
          return Promise.reject(error);
        }

        original!._authRetried = true;
        const refreshed = await this.forceRefresh();
        if (!refreshed) {
          this.notifyAuthFailure();
          return Promise.reject(error);
        }

        original!.headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
        return this.client(original!);
      }
    );
  }

  setTokenGetter(getter: () => AuthState | null) {
    this.getAuthState = getter;
  }

  setTokenRefreshCallback(cb: () => Promise<{ accessToken?: string } | null | undefined>) {
    this.refreshTokenCallback = cb;
  }

  /** Registered by the store: invoked once when the session can't be recovered
   *  (refresh failed), so the app can sign the user out and redirect to login. */
  setAuthFailureHandler(cb: () => void) {
    this.authFailureCallback = cb;
  }

  private notifyAuthFailure() {
    if (this.authFailureNotified) return;
    this.authFailureNotified = true;
    this.authFailureCallback?.();
  }

  private forceRefresh(): Promise<{ accessToken: string } | null> {
    const state = this.getAuthState?.();
    if (!this.refreshTokenCallback || !state?.refreshToken) return Promise.resolve(null);

    if (!this.refreshPromise) {
      this.refreshPromise = Promise.resolve()
        .then(() => this.refreshTokenCallback!())
        .then(t => (t && t.accessToken ? { accessToken: t.accessToken } : null))
        .catch(() => null)
        .finally(() => { this.refreshPromise = null; });
    }
    return this.refreshPromise;
  }

  private async ensureValidToken(): Promise<string | null> {
    const state = this.getAuthState?.();
    if (state == null || !state.accessToken || !state.expiresAt || !state.isAuthenticated) return null;

    const isExpiringSoon = DateTime.fromMillis(state.expiresAt).diffNow("milliseconds").milliseconds < 60_000;
    if (isExpiringSoon) {
      const refreshed = await this.forceRefresh();
      return refreshed?.accessToken ?? state.accessToken;
    }
    return state.accessToken;
  }

  /** The bearer token, renewed first if it is about to expire. */
  getAccessToken(): Promise<string | null> {
    return this.ensureValidToken();
  }

  renewAccessToken(): Promise<string | null> {
    return this.forceRefresh().then(refreshed => refreshed?.accessToken ?? null);
  }

  private async getAuthHeaders() {
    const token = await this.ensureValidToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async get<T>(endpoint: string, params: object, transformResponse?: (raw: object) => T, transformRequest?: (params: object) => object): Promise<T | ErrorResponse> {
    const requestParams = transformRequest ? transformRequest(params) : params;
    // Note: keyed on the parameters as well, so only an identical request supersedes this one. The
    // offline prefetcher fetches a page's unfiltered rows from the same URL as the page's filtered ones.
    const cancelKey = `${endpoint}?${JSON.stringify(requestParams)}`;
    let cancelSource: CancelTokenSource | undefined;
    try {
      // Note: the auth headers are resolved before this request claims the endpoint's cancel token,
      // so a concurrent caller can't cancel it while it is still waiting on a token refresh and has
      // nothing in flight to cancel.
      const headers = await this.getAuthHeaders();
      this.cancelPreviousRequest(cancelKey);
      cancelSource = this.createNewCancelToken(cancelKey);
      const rawResult = await this.client.get<T>(`${this.baseUrl}${endpoint}`,
        {
          params: requestParams,
          cancelToken: cancelSource.token,
          withCredentials: true,
          headers
        });
      if (transformResponse) {
        return transformResponse(rawResult.data as object);
      }
      return rawResult.data;
    } catch (error) {
      if (axios.isCancel(error))
        throw Cancelled;
      else //Note: We don't need to log request cancellations
        console.log(error)

      if (axios.isAxiosError(error) && error.response) {
        return Promise.reject(
          new ErrorResponse(error.response.status, [
            {
              code: error.response.status,
              description: error.response.data.message || i18n.t('errors.generic'),
            },
          ])
        );
      }
      return Promise.reject(
        new ErrorResponse(500, [
          {
            code: 500,
            description: i18n.t('errors.generic'),
          },
        ])
      );
    } finally {
      if (cancelSource && this.cancelTokenSources[cancelKey] === cancelSource) {
        delete this.cancelTokenSources[cancelKey];
      }
    }
  }

  async post<T, T1>(endpoint: string, data: T, options?: WriteOptions<T> & PostOptions): Promise<ActionResponse<T1 | null>> {
    try {
      if (options?.transformRequest) {
        data = options.transformRequest(data);
      }

      const result = await this.client.post<T1>(`${this.baseUrl}${endpoint}`, data, {
        withCredentials: true,
        timeout: options?.timeoutMs,
        headers: { ...(options?.skipAuth ? {} : await this.getAuthHeaders()), ...options?.headers }
      });

      options?.onHeaders?.(result.headers);
      return result.data as ActionResponse<T1>;
    } catch (error) {
      console.log(error)
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          data: null,
          errors: [
            {
              code: error.response.status,
              description: error.response.data.message || i18n.t('errors.generic'),
            },
          ]
        } as ActionResponse<T1>;
      }
      return Promise.reject(
        {
          success: false,
          data: null,
          errors: [
            {
              code: 500,
              description: i18n.t('errors.generic'),
            },
          ]
        } as ActionResponse<T1>
      );
    }
  }

  async put<TIn, TOut = TIn>(endpoint: string, data: TIn, options?: WriteOptions<TIn>): Promise<ActionResponse<TOut | null>> {
    try {
      if (options?.transformRequest) {
        data = options.transformRequest(data);
      }

      const response = await this.client.put<TOut>(`${this.baseUrl}${endpoint}`, data, {
        withCredentials: true,
        timeout: options?.timeoutMs,
        headers: { ...(await this.getAuthHeaders()), ...options?.headers }
      });

      return response.data as ActionResponse<TOut>;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          data: null,
          errors: [
            {
              code: error.response.status,
              description: error.response.data.message || i18n.t('errors.generic'),
            },
          ]
        } as ActionResponse<TOut>;
      }

      return Promise.reject(
        {
          success: false,
          data: null,
          errors: [
            {
              code: 500,
              description: i18n.t('errors.generic'),
            },
          ]
        } as ActionResponse<TOut>
      );
    }
  }

  async delete<T = null>(endpoint: string, options?: RequestOptions): Promise<ActionResponse<T>> {
    try {
      const response = await this.client.delete<ActionResponse<T>>(`${this.baseUrl}${endpoint}`, {
        withCredentials: true,
        timeout: options?.timeoutMs,
        headers: { ...(await this.getAuthHeaders()), ...options?.headers }
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          data: null,
          messages: [
            {
              code: error.response.status.toString(),
              description: error.response.data.message || i18n.t('errors.generic'),
            },
          ]
        } as ActionResponse<T>;
      }
      return Promise.reject(
        {
          success: false,
          data: null,
          messages: [
            {
              code: '500',
              description: i18n.t('errors.generic'),
            },
          ]
        } as ActionResponse<T>
      );
    }
  }

  async downloadFile(endpoint: string, filename: string): Promise<void> {
    try {
      const response = await this.client.get(`${this.baseUrl}${endpoint}`, {
        responseType: 'blob',
        headers: await this.getAuthHeaders(),
        withCredentials: true
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return Promise.reject(
          new ErrorResponse(error.response.status, [
            {
              code: error.response.status,
              description: error.response.data.message || i18n.t('errors.downloadFailed'),
            },
          ])
        );
      }

      return Promise.reject(
        new ErrorResponse(500, [
          {
            code: 500,
            description: i18n.t('errors.downloadUnexpected'),
          },
        ])
      );
    }
  }

  private cancelPreviousRequest(endpoint: string) {
    if (this.cancelTokenSources[endpoint]) {
      this.cancelTokenSources[endpoint].cancel('Request cancelled due to a new request.');
    }
  }

  private createNewCancelToken(endpoint: string): CancelTokenSource {
    this.cancelTokenSources[endpoint] = axios.CancelToken.source();
    return this.cancelTokenSources[endpoint];
  }
}

export function convertDatesToUTC(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;

  if (obj instanceof FormData || obj instanceof File || obj instanceof Blob) {
    return obj;
  }

  if (obj instanceof Date) {
    return DateTime.fromJSDate(obj).toUTC().toISO();
  }

  if (DateTime.isDateTime(obj) || (typeof obj === "object" && (obj as { isLuxonDateTime?: boolean }).isLuxonDateTime === true)) {
    const candidate = obj as DateTime & { ts?: number };
    if (typeof candidate.toUTC === "function") return candidate.toUTC().toISO();
    return typeof candidate.ts === "number" ? DateTime.fromMillis(candidate.ts).toUTC().toISO() : null;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertDatesToUTC);
  }

  if (typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertDatesToUTC(value);
    }
    return result;
  }

  return obj;
}

/**
 * Iterates through an object and formats all Javascript DateTime objects to only include the date (not time) in ISO format (YYYY-MM-DD).
 * This is to match the DateOnly format in the backend.
 * If endpoint doesn't expect DateOnly or object does not have DateTime objects, then this function should not be called.
 *
 * @param obj The object to format dates in
 * @returns A new object with dates formatted
 */
export function formatDatesForDateOnlyModel<T extends object>(obj: T): T {
  function formatAsLocalDateString(date: DateTime): string {
    return date.toFormat("yyyy-MM-dd");
  }

  function recurse(value: unknown): unknown {
    if (DateTime.isDateTime(value)) {
      return value.toISODate();
    }

    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      if (parsed.isValid) {
        return formatAsLocalDateString(parsed);
      }
    }

    if (Array.isArray(value)) {
      return value.map(recurse);
    }

    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const result: Record<string, unknown> = {};
      for (const key in record) {
        result[key] = recurse(record[key]);
      }
      return result;
    }
    return value;
  }

  return recurse(obj) as T;
}

/**
 * Recursively walks an object (and any nested arrays/objects),
 * and replaces every DateTime instance with a time‐only string "HH:mm:ss".
 * Call this in your transformRequest for endpoints expecting TimeOnly values.
 */
export function formatDatesForTimeOnlyModel<T extends object>(obj: T): T {
  function recurse(value: unknown): unknown {
    if (value instanceof DateTime) {
      return value.toUTC().toFormat('HH:mm:ss');
    } else if (Array.isArray(value)) {
      return value.map(recurse);
    } else if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(record)) {
        out[k] = recurse(record[k]);
      }
      return out;
    }
    return value;
  }

  return recurse(obj) as T;
}

export const ajaxController = new AjaxController();